import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Sparkles, Sword, Target, Timer, TriangleAlert } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '../../lib/utils';
import {
  gamesAdminApi,
  type CheaterRow,
  type DuelMatchRow,
  type DuelStats,
  type EloDistributionPoint,
  type GameSessionStats,
  type GamesOverview,
  type UsagePoint,
} from '../../api/games';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { Card as UiCard } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { EmptyState, ErrorState, KpiCard, PageHeader, SegmentedControl, StatusPill, type StatusTone } from '@/components/app';
import { Card, InfoHint, TabIntro } from './controls';
import SparringTab from './SparringTab';
import LimitsTab from './LimitsTab';
import { getErrorMessage } from '@/lib/errors';
import { DUEL_STATUS_LABELS, labelOf } from '@/lib/labels';
import { formatTashkentShort } from '@/lib/datetime';
import { useTokenColors } from '@/hooks/useTokenColors';

type SubTab = 'overview' | 'usage' | 'cheaters' | 'duels' | 'sparring' | 'limits';
type UsageGame = 'daily' | 'time_attack' | 'survival';
type CheaterGame = 'time_attack' | 'survival';

const fmt = (n: number) => n.toLocaleString('ru-RU');
const shortId = (id: string) => `${id.substring(0, 8)}…`;

const TABS: Array<{ id: SubTab; label: string }> = [
  { id: 'overview', label: 'Обзор' },
  { id: 'usage', label: 'Активность' },
  { id: 'duels', label: 'Дуэли' },
  { id: 'sparring', label: 'Спарринг' },
  { id: 'limits', label: 'Лимиты' },
  { id: 'cheaters', label: 'Подозрительные' },
];

const GAME_LABELS: Record<UsageGame, string> = {
  daily: 'Вызов дня',
  time_attack: 'Тайм-атака',
  survival: 'Выживание',
};

const DUEL_STATUS_TONES: Record<string, StatusTone> = {
  active: 'pos',
  waiting: 'warn',
  completed: 'neutral',
  expired: 'neutral',
};

export default function GamesPage() {
  const [tab, setTab] = useState<SubTab>('overview');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Игры и лимиты" subtitle="Вызов дня · Тайм-атака · Выживание · Дуэль со спаррингом — все режимы работают" />

      <Tabs value={tab} onValueChange={(v) => setTab(v as SubTab)}>
        <TabsList aria-label="Раздел" className="flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="usage"><UsageTab /></TabsContent>
        <TabsContent value="duels"><DuelsTab /></TabsContent>
        <TabsContent value="sparring"><SparringTab /></TabsContent>
        <TabsContent value="limits"><LimitsTab /></TabsContent>
        <TabsContent value="cheaters"><CheatersTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────

function OverviewTab() {
  const [data, setData] = useState<GamesOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // По умолчанию метрики дуэлей считают только живые матчи — иначе рост PvP
  // не отличить от накрутки спарринг-партнёрами. См. ADR 0018.
  const [includeSparring, setIncludeSparring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await gamesAdminApi.overview(includeSparring);
      setData(res);
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Не удалось загрузить сводку'));
    } finally {
      setLoading(false);
    }
  }, [includeSparring]);

  useEffect(() => {
    load();
  }, [load]);

  const d = data?.daily;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <span className="flex items-center gap-2.5 text-sm">
          <Switch id="games-include-sparring" checked={includeSparring} onCheckedChange={setIncludeSparring} />
          <label htmlFor="games-include-sparring" className="cursor-pointer select-none font-semibold">
            Считать дуэли со спаррингом
          </label>
          <InfoHint text="По умолчанию метрики дуэлей показывают только живой PvP. Включи, чтобы увидеть общий объём вместе с матчами против спарринг-партнёров — но помни, что рост этой цифры не означает роста реального сообщества." />
        </span>
        <RefreshButton onClick={load} loading={loading} />
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      <TabIntro>
        Сводка по четырём играм. <b className="text-text">Попытка</b> — один сыгранный заход,{' '}
        <b className="text-text">DAU / WAU</b> — уникальные игроки за сутки и за неделю: один человек
        считается один раз, сколько бы раз ни играл. Сутки везде считаются по
        UTC и начинаются в 05:00 по Ташкенту. Наведи курсор на ⓘ у любой цифры,
        чтобы узнать, как она собирается.
      </TabIntro>

      <Card title="Вызов дня" icon={<Sparkles />}>
        <KpiGrid>
          <Kpi loading={loading} label="Сегодня" value={d?.todayAttempts} hint="Сколько ответов на вопрос дня получено за сегодняшние UTC-сутки." />
          <Kpi loading={loading} label="DAU" value={d?.dau} hint="Уникальных игроков в вызове дня за сегодня. Повторные заходы одного человека не удваивают цифру." />
          <Kpi loading={loading} label="WAU" value={d?.wau} hint="Уникальных игроков в вызове дня за последние 7 дней." />
          <Kpi loading={loading} label="Попыток за неделю" value={d?.weekAttempts} hint="Ответов на вопрос дня за 7 дней. Один игрок даёт по одному ответу в день, так что число близко к сумме дневных." />
          <Kpi loading={loading} label="Попыток за месяц" value={d?.monthAttempts} hint="Ответов на вопрос дня за 30 дней." />
          <Kpi loading={loading} label="Всего" value={d?.totalAttempts} hint="Ответов на вопрос дня за всё время существования игры." />
          <Kpi loading={loading} label="Игроков с серией" value={d?.playersWithStreak} hint="У скольких игроков серия сейчас не прервана. Серия рвётся, если пропустить день (премиум может заморозить её или доиграть задним числом)." />
          <Kpi loading={loading} label="Лучшая серия" value={d?.bestStreak} hint="Самая длинная серия за всю историю среди всех игроков — рекорд, а не текущее состояние." />
        </KpiGrid>
      </Card>

      <SessionGameSection
        title="Тайм-атака"
        icon={<Timer />}
        stats={data?.time_attack ?? null}
        loading={loading}
        sectionHint="Забег на минуту: игрок отвечает на как можно больше вопросов за 60 секунд. Счёт — число верных ответов."
        scoreHint="Лучший результат одного забега за всю историю игры."
      />

      <SessionGameSection
        title="Выживание"
        icon={<Target />}
        stats={data?.survival ?? null}
        loading={loading}
        sectionHint="Забег без права на ошибку: игра идёт, пока не кончатся жизни. Премиум получает один второй шанс за забег."
        scoreHint="Самая длинная серия верных ответов за всю историю игры."
      />

      <DuelOverviewSection stats={data?.duel ?? null} loading={loading} />
    </div>
  );
}

function DuelOverviewSection({ stats, loading }: { stats: DuelStats | null; loading: boolean }) {
  return (
    <Card
      title="Дуэль"
      icon={<Sword />}
      hint="Матч 1-на-1 на десяти общих вопросах. Побеждает тот, у кого больше верных; при равенстве — кто ответил быстрее. По умолчанию здесь считаются только матчи между живыми людьми."
    >
      <KpiGrid>
        <Kpi loading={loading} label="Сегодня" value={stats?.todayCompleted} hint="Матчей, доигранных до конца за сегодня. Матч считается завершённым, когда сыграли обе стороны." />
        <Kpi loading={loading} label="DAU" value={stats?.dau} hint="Уникальных игроков за сегодня." />
        <Kpi loading={loading} label="Неделя" value={stats?.weekCompleted} hint="Завершённых матчей за 7 дней." />
        <Kpi loading={loading} label="Месяц" value={stats?.monthCompleted} hint="Завершённых матчей за 30 дней." />
        <Kpi loading={loading} label="Всего завершено" value={stats?.totalCompleted} hint="Завершённых матчей за всё время." />
        <Kpi loading={loading} label="Активных матчей" value={stats?.activeMatches} hint="Матчей, где соперник найден, но кто-то ещё не доиграл. Если игрок бросит матч, он протухнет и уйдёт в архив автоматически." />
        <Kpi loading={loading} label="Ожидают" value={stats?.waitingMatches} hint="Матчей, ждущих соперника. После включения спарринга это число должно держаться около нуля: партнёр подсаживается за секунды, а зависшие добивает крон." />
      </KpiGrid>
    </Card>
  );
}

function SessionGameSection({
  title,
  icon,
  stats,
  loading,
  sectionHint,
  scoreHint,
}: {
  title: string;
  icon: React.ReactNode;
  stats: GameSessionStats | null;
  loading: boolean;
  sectionHint: string;
  scoreHint: string;
}) {
  const flagged = stats?.flaggedCount ?? 0;
  return (
    <Card title={title} icon={icon} hint={sectionHint}>
      <KpiGrid>
        <Kpi loading={loading} label="Сегодня" value={stats?.todaySessions} hint="Завершённых заходов за сегодня. Брошенные и не отправленные на сервер не считаются." />
        <Kpi loading={loading} label="DAU" value={stats?.dau} hint="Уникальных живых игроков за сегодня. Спарринг-партнёры сюда не попадают никогда, даже с включённым переключателем." />
        <Kpi loading={loading} label="WAU" value={stats?.wau} hint="Уникальных игроков за последние 7 дней." />
        <Kpi loading={loading} label="Неделя" value={stats?.weekSessions} hint="Завершённых заходов за 7 дней." />
        <Kpi loading={loading} label="Месяц" value={stats?.monthSessions} hint="Завершённых заходов за 30 дней." />
        <Kpi loading={loading} label="Всего" value={stats?.totalSessions} hint="Завершённых заходов за всё время." />
        <Kpi loading={loading} label="Лучший счёт" value={stats?.bestScoreAll} hint={scoreHint} />
        <Kpi
          loading={loading}
          label="Помечено"
          value={flagged}
          tone={flagged > 0 ? 'neg' : undefined}
          hint="Сколько заходов сервер счёл невозможными — например, слишком быстрые ответы или подделанное время. Они не попадают в лидерборд; разбор на вкладке «Подозрительные»."
        />
      </KpiGrid>
    </Card>
  );
}

// ─── Duels tab ────────────────────────────────────────────────────────────

function DuelsTab() {
  const c = useTokenColors();
  const [matches, setMatches] = useState<DuelMatchRow[]>([]);
  const [elo, setElo] = useState<EloDistributionPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, e] = await Promise.all([gamesAdminApi.duelsActive(100), gamesAdminApi.eloDistribution()]);
      setMatches(m);
      setElo(e);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Не удалось загрузить дуэли'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <TabIntro>
        Здесь видно, как игроки распределены по силе и что происходит с матчами
        прямо сейчас. <b className="text-text">ELO</b> — шахматный рейтинг: старт 1000, победа над
        сильным даёт больше очков, чем над слабым. Ранги: до 1100 бронза, 1100
        серебро, 1300 золото, 1500 платина, 1750 алмаз.
      </TabIntro>

      <div className="flex justify-end">
        <RefreshButton onClick={load} loading={loading} />
      </div>
      {error && <ErrorBanner message={error} onRetry={load} />}

      <Card
        title="Распределение ELO (корзины по 50 очков)"
        hint="Каждый столбик — сколько игроков имеют рейтинг в этом диапазоне. Здоровая картина — горка вокруг 1000. Если весь график уползает вправо, значит побеждать стало слишком легко: проверь точность спарринг-профилей."
      >
        {loading ? (
          <Skeleton className="h-48 w-full" />
        ) : elo.length === 0 ? (
          <EmptyState title="Нет данных" description="Никто ещё не играл в дуэль" className="py-8" />
        ) : (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={elo} margin={{ top: 4, right: 0, left: -18, bottom: 0 }} barCategoryGap="12%">
                <CartesianGrid stroke={c['border-soft']} vertical={false} />
                <XAxis dataKey="bucket" stroke={c.border} tick={{ fill: c['text-2'], fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: c['text-2'], fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: c.muted, opacity: 0.6 }}
                  content={({ active, payload }) =>
                    active && payload?.length ? (
                      <ChartTip
                        title={`${payload[0].payload.bucket}–${payload[0].payload.bucket + 49}`}
                        value={`${fmt(Number(payload[0].value ?? 0))} игроков`}
                      />
                    ) : null
                  }
                />
                <Bar dataKey="count" fill={c.text} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <UiCard className="overflow-hidden">
        <div className="px-[18px] py-3.5">
          <b className="font-display text-[17px] font-bold">
            Активные и ожидающие матчи
            <InfoHint text="«Ожидает» — игрок ищет соперника и ещё никого не нашёл. «Активен» — соперник есть, но матч не доигран. Игрок A всегда создатель матча, игрок B — тот, кто подключился; спарринг-партнёр всегда оказывается игроком B." />
          </b>
        </div>
        {!loading && matches.length === 0 ? (
          <EmptyState title="Нет активных матчей" className="border-t border-border-soft py-8" />
        ) : (
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead>Статус</TableHead>
                <TableHead>
                  Игрок A
                  <InfoHint text="Тот, кто нажал «Найти соперника» и создал матч." />
                </TableHead>
                <TableHead>
                  Игрок B
                  <InfoHint text="Подключившийся соперник: живой игрок или спарринг-партнёр. Пусто — соперника ещё нет." />
                </TableHead>
                <TableHead>
                  Тема
                  <InfoHint text="Дуэль по конкретной теме ПДД — премиум-режим. Пусто означает вопросы из всего банка." />
                </TableHead>
                <TableHead>Создан</TableHead>
                <TableHead>Истекает</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading
                ? <SkeletonRows rows={4} cols={6} />
                : matches.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <StatusPill tone={DUEL_STATUS_TONES[m.status] ?? 'neutral'}>{labelOf(DUEL_STATUS_LABELS, m.status)}</StatusPill>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{shortId(m.playerA)}</TableCell>
                      <TableCell className="font-mono text-xs">{m.playerB ? shortId(m.playerB) : '—'}</TableCell>
                      <TableCell className="tabular-nums">{m.topicId ?? '—'}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums text-text-2">{formatTashkentShort(m.createdAt)}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums text-text-2">{m.expiresAt ? formatTashkentShort(m.expiresAt) : '—'}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}
      </UiCard>
    </div>
  );
}

// ─── Usage chart tab ──────────────────────────────────────────────────────

const USAGE_GAMES = (Object.keys(GAME_LABELS) as UsageGame[]).map((g) => ({ value: g, label: GAME_LABELS[g] }));
const USAGE_DAYS = [7, 14, 30, 90].map((n) => ({ value: String(n), label: `${n} дн.` }));

function UsageTab() {
  const c = useTokenColors();
  const [game, setGame] = useState<UsageGame>('daily');
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<UsagePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await gamesAdminApi.usage(game, days);
      setData(res);
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Не удалось загрузить активность'));
    } finally {
      setLoading(false);
    }
  }, [game, days]);

  useEffect(() => {
    load();
  }, [load]);

  const total = data.reduce((s, d) => s + d.count, 0);

  return (
    <div className="flex flex-col gap-4">
      <TabIntro>
        График показывает, сколько заходов в игру было в каждый день выбранного
        периода. Это <b className="text-text">объём игры</b>, а не число игроков: один человек за день
        мог сыграть несколько раз. Провалы обычно означают либо праздники, либо
        поломку — сверяй их с датами релизов.
      </TabIntro>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl ariaLabel="Игра" value={game} onChange={setGame} options={USAGE_GAMES} />
        <div className="flex flex-wrap items-center gap-2.5">
          <SegmentedControl ariaLabel="Период" value={String(days)} onChange={(v) => setDays(Number(v))} options={USAGE_DAYS} />
          <RefreshButton onClick={load} loading={loading} />
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      <UiCard role="region" aria-label="Заходов в день" className="flex flex-col gap-3.5 p-[18px]">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <b className="font-display text-[17px] font-bold">
            {GAME_LABELS[game]}: заходов в день · последние {days} дн.
          </b>
          <span className="text-sm text-text-2">
            Всего: <b className="font-display tabular-nums text-text">{loading ? '—' : fmt(total)}</b>
          </span>
        </div>

        {loading ? (
          <Skeleton className="h-[220px] w-full" />
        ) : data.length === 0 ? (
          <EmptyState title="Нет данных за период" className="py-8" />
        ) : (
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 4, right: 0, left: -18, bottom: 0 }} barCategoryGap="18%">
                <CartesianGrid stroke={c['border-soft']} vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke={c.border}
                  tick={{ fill: c['text-2'], fontSize: 11 }}
                  tickLine={false}
                  minTickGap={12}
                  interval="preserveStartEnd"
                  tickFormatter={(v: string) => v.slice(5).split('-').reverse().join('.')}
                />
                <YAxis tick={{ fill: c['text-2'], fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: c.muted, opacity: 0.6 }}
                  content={({ active, payload }) =>
                    active && payload?.length ? (
                      <ChartTip
                        title={String(payload[0].payload.date).split('-').reverse().join('.')}
                        value={`${fmt(Number(payload[0].value ?? 0))} заходов`}
                      />
                    ) : null
                  }
                />
                <Bar dataKey="count" fill={c.accent} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </UiCard>
    </div>
  );
}

// ─── Cheaters tab ─────────────────────────────────────────────────────────

const CHEATER_GAMES: Array<{ value: CheaterGame; label: string }> = [
  { value: 'time_attack', label: GAME_LABELS.time_attack },
  { value: 'survival', label: GAME_LABELS.survival },
];

function CheatersTab() {
  const [game, setGame] = useState<CheaterGame>('time_attack');
  const [data, setData] = useState<CheaterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await gamesAdminApi.cheaters(game, 100);
      setData(res);
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Не удалось загрузить помеченные сессии'));
    } finally {
      setLoading(false);
    }
  }, [game]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <TabIntro tone="warning">
        Заходы, которые сервер счёл невозможными: например, ответы быстрее, чем
        человек успевает прочитать вопрос, или подделанное время игры. Такие
        сессии получают статус <b>flagged</b> — результат не идёт в лидерборд и
        в рекорды, но сам игрок не блокируется. Пустой список — это норма и
        хороший знак.
      </TabIntro>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl ariaLabel="Игра" value={game} onChange={setGame} options={CHEATER_GAMES} />
        <RefreshButton onClick={load} loading={loading} />
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      <UiCard className="overflow-hidden">
        <div className="flex items-center gap-2 px-[18px] py-3.5">
          <TriangleAlert className="size-[18px] text-warn" aria-hidden />
          <b className="font-display text-[17px] font-bold">Помеченные сессии</b>
          {!loading && data.length > 0 && <StatusPill size="sm" tone="warn">{fmt(data.length)}</StatusPill>}
        </div>

        {!loading && data.length === 0 ? (
          <EmptyState title="Нет помеченных сессий" description="Всё чисто" className="border-t border-border-soft py-8" />
        ) : (
          <Table className="min-w-[560px]">
            <TableHeader>
              <TableRow>
                <TableHead>ID пользователя</TableHead>
                <TableHead className="text-right">Счёт</TableHead>
                {game === 'time_attack' && <TableHead className="text-right">Длительность</TableHead>}
                {game === 'survival' && <TableHead>Второй шанс</TableHead>}
                <TableHead>Завершено</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading
                ? <SkeletonRows rows={4} cols={4} />
                : data.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{shortId(row.user_id)}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{row.score}</TableCell>
                      {game === 'time_attack' && (
                        <TableCell className="text-right tabular-nums">{row.duration_ms != null ? `${fmt(row.duration_ms)} мс` : '—'}</TableCell>
                      )}
                      {game === 'survival' && <TableCell>{row.used_revive ? 'да' : 'нет'}</TableCell>}
                      <TableCell className="whitespace-nowrap tabular-nums text-text-2">
                        {row.finished_at ? formatTashkentShort(row.finished_at) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}
      </UiCard>
    </div>
  );
}

// ─── Shared atoms ─────────────────────────────────────────────────────────

function KpiGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{children}</div>;
}

function Kpi({
  label,
  value,
  hint,
  loading,
  tone,
}: {
  label: string;
  value: number | undefined;
  hint?: string;
  loading: boolean;
  tone?: 'neg';
}) {
  return (
    <KpiCard
      className="bg-surface-alt shadow-none"
      label={
        <>
          {label}
          {hint && <InfoHint text={hint} />}
        </>
      }
      value={<span className={cn(tone === 'neg' && 'text-neg')}>{fmt(value ?? 0)}</span>}
      loading={loading}
    />
  );
}

function SkeletonRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i}>
          {Array.from({ length: cols }, (__, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full max-w-28" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function ChartTip({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-3.5 py-2.5 text-[13px] text-text shadow-pop">
      <p className="m-0 mb-1 font-semibold tabular-nums">{title}</p>
      <p className="m-0 tabular-nums text-text-2">{value}</p>
    </div>
  );
}

function RefreshButton({ onClick, loading }: { onClick: () => void; loading?: boolean }) {
  return (
    <Button type="button" variant="secondary" onClick={onClick} disabled={loading}>
      <RefreshCw className={cn(loading && 'animate-spin')} aria-hidden />
      Обновить
    </Button>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <ErrorState compact message={message} onRetry={onRetry} className="rounded-xl bg-neg-soft px-3.5 py-3" />;
}
